import Ajv2020 from 'ajv/dist/2020';
import addFormats from 'ajv-formats';
import { decamelizeKeys } from 'humps';

import type { SeatChangePreview, SeatChangeSchedule } from './memberSeatChange';

// eslint-disable-next-line import/extensions -- Preserve published JSON contract filenames.
import schema from '@/testing/fixtures/member-seat-change-prevention-v1/contract.schema.json';
// eslint-disable-next-line import/extensions -- Preserve published JSON contract filenames.
import legacySchema from '@/testing/fixtures/member-seat-change-v1/contract.schema.json';

const ajv = new Ajv2020({ strict: false });
addFormats(ajv);
ajv.addSchema(
  legacySchema,
  'https://contracts.invalid/member-seat-change-v1/contract.schema.json',
);
ajv.addSchema(
  schema,
  'https://contracts.invalid/member-seat-change-prevention-v1/contract.schema.json',
);
const validate = ajv.compile({
  $ref: 'https://contracts.invalid/member-seat-change-prevention-v1/contract.schema.json#/$defs/preview',
});

export const validSeatChangeSchedule = (
  value: unknown,
): value is SeatChangeSchedule => {
  if (!value || typeof value !== 'object') return false;
  const schedule = value as Partial<SeatChangeSchedule>;
  if (
    typeof schedule.startAt !== 'string' ||
    typeof schedule.endAt !== 'string' ||
    typeof schedule.timezone !== 'string'
  )
    return false;
  if (!(Date.parse(schedule.endAt) > Date.parse(schedule.startAt)))
    return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: schedule.timezone }).format(
      new Date(schedule.startAt),
    );
    return true;
  } catch {
    return false;
  }
};

export const isSeatChangePreview = (
  value: unknown,
): value is SeatChangePreview => {
  if (!validate(decamelizeKeys(value as object))) return false;
  const preview = value as SeatChangePreview;
  const ids = preview.occurrences.map((occurrence) => occurrence.appointmentId);
  const sessions = preview.occurrences.flatMap((occurrence) =>
    occurrence.sessionId ? [occurrence.sessionId] : [],
  );
  const targetMinutes = preview.targetSeatType === 'executive' ? 60 : 45;
  return (
    new Set(ids).size === ids.length &&
    new Set(sessions).size === sessions.length &&
    preview.previousSeatType !== preview.targetSeatType &&
    Date.parse(preview.expiresAt) > Date.parse(preview.effectiveAt) &&
    preview.occurrences.every(
      (occurrence) =>
        validSeatChangeSchedule(occurrence.originalSchedule) &&
        validSeatChangeSchedule(occurrence.targetSchedule) &&
        occurrence.originalSchedule.startAt ===
          occurrence.targetSchedule.startAt &&
        occurrence.originalSchedule.timezone ===
          occurrence.targetSchedule.timezone &&
        Date.parse(occurrence.originalSchedule.startAt) >
          Date.parse(preview.effectiveAt) &&
        Date.parse(occurrence.targetSchedule.endAt) -
          Date.parse(occurrence.targetSchedule.startAt) ===
          targetMinutes * 60000 &&
        (occurrence.status === 'ready'
          ? occurrence.reason === null
          : !!occurrence.reason?.trim()) &&
        (!occurrence.canWaiveBuffer ||
          (occurrence.status === 'buffer_conflict' &&
            occurrence.bufferBeforeMinutes + occurrence.bufferAfterMinutes >
              0)),
    )
  );
};

export const seatChangeReviewErrorMessage = (error: unknown): string => {
  const code = (error as { data?: { code?: string } } | null)?.data?.code;
  switch (code) {
    case 'seat_change_preview_stale':
      return 'Calendar review expired or changed. Request a fresh review.';
    case 'seat_change_conflict':
      return 'Calendar conflicts block this seat change. Resolve the affected dates, then Request a fresh review.';
    case 'seat_change_preview_unavailable':
      return 'Calendar review is unavailable. Request a fresh review.';
    default:
      return 'Seat change could not be confirmed. Request a fresh review.';
  }
};

export const isUncertainSeatChangeSave = (error: unknown): boolean => {
  const failure = error as {
    status?: number | string;
    data?: { code?: string };
  } | null;
  if (
    [
      'seat_change_preview_stale',
      'seat_change_conflict',
      'seat_change_preview_unavailable',
    ].includes(failure?.data?.code ?? '')
  )
    return false;
  return typeof failure?.status !== 'number' || failure.status >= 500;
};
